import { useCallback, useEffect, useState } from "react";
import { GOOGLE_RECAPTCHA_URL } from "configs/links";

interface UseGoogleRecaptcha {
  executeGoogleRecaptcha: () => Promise<string>;
  isGoogleRecaptchaLoaded: boolean;
}

interface GoogleRecaptcha {
  execute: (key: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha: GoogleRecaptcha;
  }
}

const RECAPTCHA_KEY = process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY;
const SCRIPT_ID = "google-recaptcha-script";
const SCRIPT_SOURCE = `${GOOGLE_RECAPTCHA_URL}?render=${RECAPTCHA_KEY}`;

const useGoogleRecaptcha = (): UseGoogleRecaptcha => {
  const [googleRecaptcha, setGoogleRecaptcha] = useState<GoogleRecaptcha>();

  useEffect(() => {
    const onLoadHandler = () => {
      setGoogleRecaptcha(window.grecaptcha);
    };

    const script = document.createElement("script");
    script.src = SCRIPT_SOURCE;
    script.id = SCRIPT_ID;
    script.defer = true;
    script.addEventListener("load", onLoadHandler);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", onLoadHandler);
    };
  }, []);

  const executeAsync = useCallback(async (): Promise<string> => {
    if (!googleRecaptcha || !RECAPTCHA_KEY) {
      return "";
    }

    try {
      const token = await googleRecaptcha.execute(RECAPTCHA_KEY, {
        action: "submit",
      });

      return token;
    } catch (error) {
      return "";
    }
  }, [googleRecaptcha]);

  return {
    executeGoogleRecaptcha: executeAsync,
    isGoogleRecaptchaLoaded: !!googleRecaptcha,
  };
};

export default useGoogleRecaptcha;
